import {
  EditOutlined,
  FileImageOutlined,
  LoadingOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  ShareAltOutlined
} from '@ant-design/icons';

import {
  Avatar,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Tooltip,
  Upload,
  message
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import React, { useRef, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { Label, Subtitle1 } from '../../components';
import CustomAutocomplete from '../../components/CustomAutocomplete';
import ResponsiveContainer from '../../components/ResponsiveContainer';
import TableAndFilters from '../../components/TableAndFilters';
import { compressionOptions } from '../../constants';
import { getBase64 } from '../../forms/vehiculo/SolicitudVehiculoForm';
import landingColorConfig from '../../modelos/landing/landing-config';
import { centralizarComponente } from '../../modelos/landing/LandingPrincipal';
import LandingPrincipalTop from '../../modelos/landing/LandingPrincipalTop';

import {
  errorHandler,
  heicAndheifConvert,
  myImageCompression
} from '../../utils';
import { instanceRef } from '../../utils/axios';

const CanalEmpresa = () => {
  const {
    params: { id: idEmpresa }
  } = useRouteMatch();

  const tableRef = useRef();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [loadingImagen, setLoadingImagen] = useState(false);
  const [imagenCargada, setImagenCargada] = useState(null);
  const [imagenActualizada, setImagenActualizada] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loadingLogo, setLoadingLogo] = useState(false);

  const [form] = Form.useForm();
  const formatosImagen = 'image/jpeg,image/heif,image/heic,image/png';

  const beforeUpload = async (file) => {
    const isValidFormat = [
      'image/jpeg',
      'image/heif',
      'image/heic',
      'image/png'
    ].includes(file.type);
    if (!isValidFormat) {
      message.warning('El formato del archivo no está permitido.');
      return false;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.warning('El tamaño de la imagen debe ser inferior a 10MB.');
      return false;
    }
    return true;
  };

  const uploadFile = async ({ file }) => {
    setLoadingImagen(true);
    try {
      const convertedFile = await heicAndheifConvert(file);
      const compressedFile = await myImageCompression(
        convertedFile,
        compressionOptions
      );
      getBase64(compressedFile, (imageUrl) => {
        setImagenCargada({
          imagen: imageUrl,
          extension: compressedFile.type.split('/')[1],
          nombre: `canal.${compressedFile.type.split('/')[1]}`
        });
        if (editData && editData.id) {
          setImagenActualizada(true);
        }

        setLoadingImagen(false);
      });
    } catch (error) {
      errorHandler(error);
      setLoadingImagen(false);
    }
  };

  const handleOpenUrl = (url) => {
    history.push(url);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    form.resetFields();
    setImagenCargada(null);
    setEditData(null);
  };

  const handleFormSubmit = async (values) => {
    setLoading(true);
    try {
      const url = editData ? 'canal/update' : 'canal/add';
      const response = await instanceRef.instance.post(url, {
        ...values,
        idEmpresa,
        files: imagenCargada ? [imagenCargada] : [],
        id: editData ? editData.id : null,
        imagenModificada: imagenActualizada,
        idSucursal: values.sucursal?.value
      });
      handleClose();
      tableRef.current.reload();
      return response.data?.data;
    } catch (error) {
      errorHandler(error);
      handleClose();
      return null;
    } finally {
      handleClose();
      setLoading(false);
    }
  };

  const getCanalImagen = async (idCanal) => {
    try {
      setLoadingLogo(true);
      const response = await instanceRef.instance.post('/canal/file', {
        id: idCanal
      });
      if (response.data?.fileBase64) {
        return response.data?.fileBase64;
      }
      return null;
    } catch (error) {
      errorHandler(error);
      return null;
    } finally {
      setLoadingLogo(false);
    }
  };

  const handleEdit = async (record) => {
    setEditData(record);
    const canalToEdit = {
      ...record,
      sucursal: {
        value: record?.sucursal?.id,
        label: record?.sucursal?.descripcion
      }
    };
    form.setFieldsValue(canalToEdit);

    const imagenObtenida = await getCanalImagen(canalToEdit.id);
    if (imagenObtenida) {
      setImagenCargada({ imagen: imagenObtenida });
    }
    setImagenActualizada(false);
    setIsModalOpen(true);
  };

  const mapOptions = ({ id: value, descripcion: label }) => ({
    value,
    label
  });

  const fetchListSucursal = (searchText) => {
    return instanceRef.instance.get(`/sucursal/search`, {
      params: {
        current: 1,
        pageSize: 10,
        filters: {
          descripcion: searchText
        }
      }
    });
  };

  const verificarCodigo = async (value) => {
    try {
      const response = await instanceRef.instance.post('/canal/codigo', {
        params: { codigo: value }
      });
      if (response.data?.data?.existe) {
        throw new Error('El código ya está en uso');
      }
    } catch (err) {
      throw new Error(err.message || 'Error validando el código');
    }
    return Promise.resolve();
  };

  const columns = [
    { title: 'Código', dataIndex: 'codigo', key: 'codigo' },
    { title: 'Nombre', dataIndex: 'nombre', key: 'nombre' },
    { title: 'Descripción', dataIndex: 'descripcion', key: 'descripcion' },
    {
      title: 'Sucursal',
      dataIndex: ['sucursal', 'descripcion'],
      key: 'sucursal'
    },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar" key={record.id}>
            <Button
              type="text"
              icon={<EditOutlined />}
              size="middle"
              loading={loadingLogo}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip
            title="Gestión de enlaces para canales"
            key={`enlace${record.id}`}
          >
            <Button
              type="text"
              icon={<ShareAltOutlined />}
              size="middle"
              onClick={() =>
                handleOpenUrl(
                  `/canal-config-empresa/enlace-canales/${idEmpresa}/${record.id}`
                )
              }
              disabled={record.enlace_corto}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div>
      <TableAndFilters
        ref={tableRef}
        withGeneralFilter
        url="/canal"
        title="Canales"
        columns={columns}
        showTable
        hasExportar
        extra={[
          <Button
            key="canalAdd"
            type="dashed"
            shape="round"
            icon={<PlusCircleOutlined />}
            size="middle"
            onClick={() => setIsModalOpen(true)}
          >
            Agregar Canal
          </Button>
        ]}
      />

      <Modal
        title={editData ? 'Editar Canal' : 'Agregar Canal'}
        open={isModalOpen}
        onCancel={handleClose}
        width={1300}
        footer={[
          <Button
            key="cancelar"
            type="secondary"
            onClick={() => handleClose()}
            disabled={loading}
          >
            Cancelar
          </Button>,
          <Button
            key="registrar"
            type="primary"
            onClick={() => form.validateFields().then(handleFormSubmit)}
            loading={loading}
          >
            {editData ? 'Actualizar' : 'Guardar'}
          </Button>
        ]}
      >
        <Spin spinning={loading} size="large" tip="Cargando...">
          <Form form={form} layout="vertical">
            <Form.Item
              name="codigo"
              label="Código"
              rules={[
                { required: true, message: 'Este campo es requerido' },
                !editData && {
                  validator: async (_, value) => {
                    if (!value) return Promise.resolve();
                    return verificarCodigo(value);
                  }
                }
              ]}
            >
              <Input disabled={editData} />
            </Form.Item>
            <Form.Item
              label="Sucursal"
              name="sucursal"
              rules={[{ required: true }]}
            >
              <CustomAutocomplete
                placeholder="Buscar Sucursal"
                fetchOptions={fetchListSucursal}
                mapOptions={mapOptions}
              />
            </Form.Item>
            <Form.Item
              name="nombre"
              label="Nombre (uso interno, no visible al público)"
              rules={[{ required: true, message: 'Este campo es requerido' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="descripcion"
              label="Descripción (uso interno, no visible al público)"
              rules={[{ required: true, message: 'Este campo es requerido' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="titulo"
              label="Título en la landing (visible públicamente)"
              rules={[{ required: true, message: 'Este campo es requerido' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="texto"
              label="Texto descriptivo en la landing (visible públicamente)"
              rules={[{ required: true, message: 'Este campo es requerido' }]}
            >
              <TextArea />
            </Form.Item>
            <Label>Imagen de Landing (visible públicamente)</Label>
            <Upload
              accept={formatosImagen}
              listType="picture-card"
              showUploadList={false}
              customRequest={uploadFile}
              beforeUpload={beforeUpload}
              disabled={loadingImagen}
            >
              {imagenCargada ? (
                <img
                  src={imagenCargada.imagen}
                  alt="avatar"
                  className="upload-img"
                />
              ) : (
                <div className="upload-img-wrapper">
                  {loadingImagen ? <LoadingOutlined /> : <PlusOutlined />}
                  {!loadingImagen && (
                    <>
                      {' '}
                      <div className="ant-upload-text">(Agregar Imagen)</div>
                      <Avatar size="large" icon={<FileImageOutlined />} />{' '}
                    </>
                  )}
                </div>
              )}
            </Upload>

            <Subtitle1>Vista previa de Landing</Subtitle1>

            <Card
              style={{
                backgroundColor: landingColorConfig.color_background
              }}
              bodyStyle={{ padding: 0 }}
            >
              <ResponsiveContainer>
                <Form.Item noStyle shouldUpdate>
                  {({ getFieldValue }) => (
                    <LandingPrincipalTop
                      centralizarComponente={centralizarComponente}
                      tipoLanding="CARTA-VERDE"
                      loading={loadingImagen}
                      // goToCotizar
                      imagenSrc={imagenCargada?.imagen}
                      enlace={{
                        canal: {
                          titulo:
                            getFieldValue('titulo') ||
                            '<Título en la landing >',
                          texto:
                            getFieldValue('texto') ||
                            '<Texto descriptivo en la landing>'
                        }
                      }}
                      preview
                    />
                  )}
                </Form.Item>
              </ResponsiveContainer>
            </Card>
          </Form>
        </Spin>
      </Modal>
    </div>
  );
};

export default CanalEmpresa;
